import { useUIStore } from "@/stores";
import { TrialOfferUnit } from "@/types";
import { formatEmail } from "@/utils";
import { REGEX, VALIDATE } from "@/utils";
import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
} from "@mui/material";
import { FC, FormEvent, useState } from "react";
import { z } from "zod";
import { useCreateAffiliateUser } from "../../queries";
import { CreateAffiliateProps } from "./create-affiliate.types";

const initialFormState = {
  fullName: "",
  phoneNumber: "",
  email: "",
  socialUrl: "",
  museReferralPaymentCents: "",
  alphaReferralPaymentBps: "",
  bankAccountCredentials: "",
  trialOfferAmount: "",
  trialOfferUnit: undefined,
};

const formSchema = z.object({
  fullName: z.string().min(1, "Field is required").max(50, "Max 50 characters"),
  email: z.string().min(1, "Field is required").email("Wrong format"),
  phoneNumber: z
    .string()
    .min(1, "Field is required")
    .refine((phoneNumber) => VALIDATE.phoneNumber(phoneNumber), "Wrong format"),
  socialUrl: z.string().min(1, "Field is required").url("Must be a valid link"),
  museReferralPaymentCents: z
    .string()
    .min(1, "Field is required")
    .regex(/^\d{1,3}$/, "Max 3 digits"),
  alphaReferralPaymentBps: z
    .string()
    .min(1, "Field is required")
    .regex(/^\d{1,3}$/, "Max 3 digits"),
  bankAccountCredentials: z
    .string()
    .min(1, "Field is required")
    .max(500, "Max 500 characters"),
  trialOfferAmount: z.string().optional(),
  trialOfferUnit: z.enum(["days", "months"]).optional(),
});

type FormData = z.infer<typeof formSchema>;

const CreateAffiliate: FC<CreateAffiliateProps> = () => {
  const { closeModal } = useUIStore();
  const { createAffiliateUser, createAffiliateUserLoading } =
    useCreateAffiliateUser();

  const [includesTrialOffer, setIncludesTrialOffer] = useState(false);
  const [touched, setTouched] = useState<Record<keyof FormData, boolean>>({
    fullName: false,
    email: false,
    phoneNumber: false,
    socialUrl: false,
    museReferralPaymentCents: false,
    alphaReferralPaymentBps: false,
    bankAccountCredentials: false,
    trialOfferAmount: false,
    trialOfferUnit: false,
  });
  const [adminUserFormData, setAdminUserFormData] = useState<Partial<FormData>>(
    {}
  );

  const formData: FormData = {
    ...initialFormState,
    ...adminUserFormData,
  };

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const body = structuredClone({
      ...formData,
      trialOfferAmount: formData?.trialOfferAmount
        ? +formData.trialOfferAmount
        : undefined,
      museReferralPaymentCents: +formData.museReferralPaymentCents * 100,
      alphaReferralPaymentBps: +formData.alphaReferralPaymentBps * 100,
    });

    if (!includesTrialOffer) {
      body.trialOfferUnit = undefined;
      body.trialOfferAmount = undefined;
    }

    createAffiliateUser(body);
  };
  const validate = () => {
    const res = formSchema.safeParse(formData);
    if (res.success) {
      return undefined;
    }
    return res.error.format();
  };

  const errors = validate();

  const isFormFilled = Object.entries(formData).every(([key, value]) => {
    if (
        !includesTrialOffer &&
        (key === "trialOfferAmount" || key === "trialOfferUnit")
    ) {
      return true;
    }
    return !!value;
  });

  return (
    <Box component={"form"} onSubmit={onSubmit}>
      <Stack spacing={2}>
        <TextField
          required
          placeholder="Full Name"
          label={
            touched.fullName && errors?.fullName
              ? errors?.fullName?._errors[0]
              : "Full Name"
          }
          onBlur={() => setTouched((prev) => ({ ...prev, fullName: true }))}
          error={touched.fullName ? !!errors?.fullName?._errors.length : false}
          value={formData.fullName}
          onChange={(e) => {
            setAdminUserFormData((prev) => ({
              ...prev,
              fullName: e.target.value,
            }));
            if (touched.fullName) {
              validate();
            }
          }}
        />
        <TextField
          required
          fullWidth
          variant="outlined"
          placeholder="Phone Number"
          label={
            touched.phoneNumber && errors?.phoneNumber
              ? errors?.phoneNumber?._errors[0]
              : "Phone Number"
          }
          onBlur={() => setTouched((prev) => ({ ...prev, phoneNumber: true }))}
          error={
            touched.phoneNumber ? !!errors?.phoneNumber?._errors.length : false
          }
          value={formData.phoneNumber}
          onChange={(e) => {
            let value = e.target.value;

            if (value && !value.startsWith("+")) {
              value = "+" + value;
            }
            if (!REGEX.PLUS_NUMBERS.test(value)) return;

            setAdminUserFormData((prev) => ({
              ...prev,
              phoneNumber: value,
            }));
            if (touched.phoneNumber) {
              validate();
            }
          }}
        />
        <TextField
          required
          fullWidth
          variant="outlined"
          placeholder="Email address"
          label={
            touched.email && errors?.email
              ? errors?.email?._errors[0]
              : "Email Address"
          }
          onBlur={() => setTouched((prev) => ({ ...prev, email: true }))}
          error={touched.email ? !!errors?.email?._errors.length : false}
          value={formData.email}
          onChange={(e) => {
            setAdminUserFormData((prev) => ({
              ...prev,
              email: formatEmail(e.target.value),
            }));
            if (touched.email) {
              validate();
            }
          }}
          slotProps={{
            htmlInput: {
              autoCapitalize: "none",
            },
          }}
        />
        <TextField
          fullWidth
          placeholder="Web or Social URL"
          required
          variant="outlined"
          label={
            touched.socialUrl && errors?.socialUrl
              ? errors?.socialUrl?._errors[0]
              : "Web or Social "
          }
          onBlur={() => setTouched((prev) => ({ ...prev, socialUrl: true }))}
          error={
            touched.socialUrl ? !!errors?.socialUrl?._errors.length : false
          }
          value={formData.socialUrl}
          onChange={(e) => {
            setAdminUserFormData((prev) => ({
              ...prev,
              socialUrl: e.target.value,
            }));
            if (touched.socialUrl) {
              validate();
            }
          }}
        />

        <TextField
          fullWidth
          required
          placeholder="Woman Referral Payment ($)"
          variant="outlined"
          label={
            touched.museReferralPaymentCents && errors?.museReferralPaymentCents
              ? errors?.museReferralPaymentCents?._errors[0]
              : "Woman Referral Payment ($)"
          }
          onBlur={() =>
            setTouched((prev) => ({ ...prev, museReferralPaymentCents: true }))
          }
          error={
            touched.museReferralPaymentCents
              ? !!errors?.museReferralPaymentCents?._errors.length
              : false
          }
          value={formData.museReferralPaymentCents}
          onChange={(e) => {
            const { value } = e.target;

            if (!REGEX.ONLY_NUMBERS.test(e.target.value) && value.length !== 0)
              return;

            setAdminUserFormData((prev) => ({
              ...prev,
              museReferralPaymentCents: e.target.value,
            }));
            if (touched.museReferralPaymentCents) {
              validate();
            }
          }}
        />
        <TextField
          fullWidth
          placeholder="Man Referral Payment (%)"
          required
          variant="outlined"
          label={
            touched.alphaReferralPaymentBps && errors?.alphaReferralPaymentBps
              ? errors?.alphaReferralPaymentBps?._errors[0]
              : "Man Referral Payment (%)"
          }
          onBlur={() =>
            setTouched((prev) => ({ ...prev, alphaReferralPaymentBps: true }))
          }
          error={
            touched.alphaReferralPaymentBps
              ? !!errors?.alphaReferralPaymentBps?._errors.length
              : false
          }
          value={formData.alphaReferralPaymentBps}
          onChange={(e) => {
            const { value } = e.target;

            if (value === "") {
              setAdminUserFormData((prev) => ({
                ...prev,
                alphaReferralPaymentBps: "",
              }));
              return;
            }

            if (
              !REGEX.ONLY_NUMBERS.test(e.target.value) ||
              Number(value) > 100
            ) {
              return;
            }

            setAdminUserFormData((prev) => ({
              ...prev,
              alphaReferralPaymentBps: e.target.value,
            }));
            if (touched.alphaReferralPaymentBps) {
              validate();
            }
          }}
        />
        <TextField
          multiline
          placeholder="Bank account credentials"
          slotProps={{ htmlInput: { maxLength: 500 } }}
          minRows={5}
          required
          variant="outlined"
          label={
            touched.bankAccountCredentials && errors?.bankAccountCredentials
              ? errors?.bankAccountCredentials?._errors[0]
              : "Bank account credentials"
          }
          onBlur={() =>
            setTouched((prev) => ({ ...prev, bankAccountCredentials: true }))
          }
          error={
            touched.bankAccountCredentials
              ? !!errors?.bankAccountCredentials?._errors.length
              : false
          }
          value={formData.bankAccountCredentials}
          onChange={(e) => {
            setAdminUserFormData((prev) => ({
              ...prev,
              bankAccountCredentials: e.target.value,
            }));
            if (touched.bankAccountCredentials) {
              validate();
            }
          }}
        />

        <Stack
          direction={"row"}
          spacing={2}
          width={"100%"}
          alignItems={"center"}
          justifyContent={"space-between"}
        >
          <Divider sx={{ borderColor: "#333", width: "calc(100% - 56px)" }}>
            Trial Offer
          </Divider>
          <FormControlLabel
            label=""
            labelPlacement="start"
            control={
              <Checkbox
                checked={includesTrialOffer}
                onChange={(e) => setIncludesTrialOffer(e.currentTarget.checked)}
              />
            }
          />
        </Stack>

        <FormControl disabled={!includesTrialOffer} fullWidth>
          <InputLabel>Period</InputLabel>
          <Select
            value={formData.trialOfferUnit || ""}
            label="Trial Period"
            onChange={({ target: { value } }) => {
              if (!value) return;

              setAdminUserFormData((prev) => ({
                ...prev,
                trialOfferUnit: value as TrialOfferUnit,
              }));
            }}
          >
            <MenuItem value="days">Days</MenuItem>
            <MenuItem value="months">Months</MenuItem>
          </Select>
        </FormControl>

        <TextField
          fullWidth
          placeholder="Amount of period"
          variant="outlined"
          disabled={!includesTrialOffer}
          label={
            touched.trialOfferAmount && errors?.trialOfferAmount
              ? errors?.trialOfferAmount?._errors[0]
              : "Amount of period"
          }
          onBlur={() =>
            setTouched((prev) => ({ ...prev, trialOfferAmount: true }))
          }
          error={
            touched.trialOfferAmount
              ? !!errors?.trialOfferAmount?._errors.length
              : false
          }
          value={formData.trialOfferAmount}
          onChange={(e) => {
            const { value } = e.target;

            if (!REGEX.ONLY_NUMBERS.test(value)) return;

            setAdminUserFormData((prev) => ({
              ...prev,
              trialOfferAmount: value,
            }));
            if (touched.trialOfferAmount) {
              validate();
            }
          }}
        />
      </Stack>

      <Stack direction={"row"} spacing={2} mt={2}>
        <Button
          fullWidth
          variant="contained"
          color="primary"
          onClick={closeModal}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          fullWidth
          variant="contained"
          color="secondary"
          loading={createAffiliateUserLoading}
          disabled={createAffiliateUserLoading || !isFormFilled || !!errors}
        >
          Create
        </Button>
      </Stack>
    </Box>
  );
};

export { CreateAffiliate };
